import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { clamp } from '@shared/helpers/math.helpers';

const WINDOW_PADDING = 10;
// Concentric with the composer's 16px corners, 5px inset and 1px window border.
const WINDOW_RADIUS = 22;
const MIN_WIDTH = 280;
const MAX_WIDTH = 1500;
const MIN_HEIGHT = 200;
const MAX_HEIGHT = 2000;
type ResizeEdge = 'top' | 'left' | 'corner';
const RESIZE_AXES = {
  top: { width: false, height: true, cursor: 'ns-resize' },
  left: { width: true, height: false, cursor: 'ew-resize' },
  corner: { width: true, height: true, cursor: 'nwse-resize' },
} satisfies Record<
  ResizeEdge,
  { width: boolean; height: boolean; cursor: string }
>;

export type ChatWindowSize = { width: number; height: number };
const useStyles = makeStyles()((theme) => ({
  overlay: {
    position: 'fixed',
    right: WINDOW_PADDING,
    bottom: WINDOW_PADDING,
    maxWidth: `calc(100% - ${WINDOW_PADDING * 2}px)`,
    maxHeight: `calc(100% - ${WINDOW_PADDING * 2}px)`,
    display: 'flex',
    flexDirection: 'column',
    zIndex: theme.zIndex.tooltip,
    backgroundColor: '#fffffd',
    borderRadius: WINDOW_RADIUS,
    border: '1px solid rgba(0,0,0,0.2)',
    boxShadow: '0px 1px 15px 0px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  header: {
    height: 44,
    paddingTop: 6,
    paddingBottom: 2,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 20,
    paddingRight: 8,
  },
  content: {
    flex: '1 1 0',
    minHeight: 0,
    overflowY: 'auto',
    overscrollBehaviorY: 'contain',
    padding: '8px 0 12px',
    borderTop: '1px solid transparent',
  },
  contentScrolled: { borderTopColor: 'rgba(0,0,0,0.1)' },
  resizeTop: {
    position: 'absolute',
    top: 0,
    left: 8,
    right: 8,
    height: 6,
    cursor: 'ns-resize',
    zIndex: 1,
  },
  resizeLeft: {
    position: 'absolute',
    top: 8,
    left: 0,
    bottom: 8,
    width: 6,
    cursor: 'ew-resize',
    zIndex: 1,
  },
  resizeCorner: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 14,
    height: 14,
    cursor: 'nwse-resize',
    zIndex: 2,
  },
}));

/** The floating conversation window shared by agent chats and activity feeds. */
export const ChatWindow: React.FC<
  React.PropsWithChildren<{
    variant?: 'floating' | 'inline';
    width?: number;
    height?: number;
    className?: string;
    onResize?: (size: ChatWindowSize) => void;
  }>
> = ({
  variant = 'floating',
  width,
  height,
  className,
  onResize,
  children,
}) => {
  const { classes, cx } = useStyles();
  const frame = useRef<HTMLDivElement>(null);
  const stopResize = useRef<(() => void) | null>(null);
  useEffect(() => () => stopResize.current?.(), []);

  const startResize = (edge: ResizeEdge) => (event: React.MouseEvent) => {
    if (event.button !== 0 || !frame.current || !onResize) return;
    event.preventDefault();
    event.stopPropagation();
    stopResize.current?.();
    const axes = RESIZE_AXES[edge];
    const start = frame.current.getBoundingClientRect();
    const startX = event.clientX;
    const startY = event.clientY;
    const previousCursor = document.body.style.cursor;
    document.body.style.cursor = axes.cursor;
    const move = (next: MouseEvent) => {
      onResize({
        width: axes.width
          ? clamp(start.width + startX - next.clientX, MIN_WIDTH, MAX_WIDTH)
          : start.width,
        height: axes.height
          ? clamp(start.height + startY - next.clientY, MIN_HEIGHT, MAX_HEIGHT)
          : start.height,
      });
    };
    const stop = () => {
      document.body.style.cursor = previousCursor;
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', stop, true);
      document.removeEventListener('pointerup', stop, true);
      document.removeEventListener('pointercancel', stop, true);
      window.removeEventListener('blur', stop);
      stopResize.current = null;
    };
    stopResize.current = stop;
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', stop, true);
    document.addEventListener('pointerup', stop, true);
    document.addEventListener('pointercancel', stop, true);
    window.addEventListener('blur', stop);
  };
  return (
    <Box
      ref={frame}
      data-chat-window={variant}
      className={cx(variant === 'floating' && classes.overlay, className)}
      style={variant === 'floating' ? { width, height } : undefined}
    >
      {variant === 'floating' && onResize && (
        <>
          <Box
            data-chat-resize="corner"
            className={classes.resizeCorner}
            onMouseDown={startResize('corner')}
          />
          <Box
            data-chat-resize="top"
            className={classes.resizeTop}
            onMouseDown={startResize('top')}
          />
          <Box
            data-chat-resize="left"
            className={classes.resizeLeft}
            onMouseDown={startResize('left')}
          />
        </>
      )}
      {children}
    </Box>
  );
};

/** Shared header geometry for floating agent conversations. */
export const ChatWindowHeader: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { classes } = useStyles();
  return <Box className={classes.header}>{children}</Box>;
};

/** The scrollable transcript and its divider below the fixed header. */
export const ChatWindowContent: React.FC<
  React.PropsWithChildren<{
    scrollRef?: React.RefObject<HTMLDivElement | null>;
    className?: string;
    onScroll?: React.UIEventHandler<HTMLDivElement>;
  }>
> = ({ scrollRef, className, onScroll, children }) => {
  const { classes, cx } = useStyles();
  const localRef = useRef<HTMLDivElement>(null);
  const ref = scrollRef ?? localRef;
  const [scrolled, setScrolled] = useState(false);
  useLayoutEffect(() => {
    setScrolled((ref.current?.scrollTop ?? 0) > 0);
  }, [children, ref]);
  return (
    <Box
      ref={ref}
      data-chat-content
      className={cx(
        classes.content,
        scrolled && classes.contentScrolled,
        className,
      )}
      onScroll={(event) => {
        setScrolled(event.currentTarget.scrollTop > 0);
        onScroll?.(event);
      }}
    >
      {children}
    </Box>
  );
};
